/**
 * OCP-365 - client-side mirror of the API's pool_tag/server_role
 * consistency rule (apps/api/src/services/vps-servers.ts). Used by the
 * server register/edit forms to preset (and lock) the monitoring role
 * from the chosen placement pool tag, so the operator can't submit an
 * inconsistent pair. The API enforces the same rule authoritatively -
 * this is the pre-submit UX guard.
 *
 * `pool_tag` governs PLACEMENT; `server_role` governs MONITORING. The
 * convention:
 *   - TENANT-SERVING pools ('tenant-pool', the demo pool, 'dedicated-*')
 *     -> server_role MUST be 'tenant'.
 *   - NON-TENANT pools ('infra', 'storage', 'control-plane') -> server_role
 *     MUST NOT be 'tenant'.
 *   - OCP-807: pool 'control-plane' <=> role 'control-plane' (both ways).
 *   - Every other tag (e.g. 'standard') is unconstrained.
 */
import type { ServerRole } from '@orboto/ocp-shared';

export type { ServerRole };
export { SERVER_ROLES, isTenantServingRole } from '@orboto/ocp-shared';

/** Default demo pool tag - mirrors settings `tenant.demo_pool_tag`. */
export const DEFAULT_DEMO_POOL_TAG = 'demo';

/** OCP-428 - one selectable placement pool in the wizard-first picker. */
export interface KnownPool {
  value: string;
  /** Short human label for the option row. */
  label: string;
  /** One-line explanation (placement target / demo / non-tenant). */
  description: string;
}

/**
 * OCP-428 - the registry of known placement pools that drives the shared
 * pool_tag picker in both the provision and register flows. It is
 * setting-driven, not a hardcoded array: the demo option reads the LIVE
 * `tenant.demo_pool_tag` value, so renaming that setting renames the
 * option too. Arbitrary tags (e.g. `dedicated-acme` single-tenant pools,
 * or any legacy `standard` tag) remain reachable via the picker's
 * free-text "custom / advanced" escape hatch - they are just not
 * first-class rows here.
 */
export function knownPoolOptions(demoPoolTag: string = DEFAULT_DEMO_POOL_TAG): KnownPool[] {
  return [
    {
      value: 'tenant-pool',
      label: 'tenant-pool',
      description: 'Default placement target - accepts new tenants',
    },
    {
      value: demoPoolTag,
      label: `${demoPoolTag} (demo)`,
      description: 'Isolated demo-tenant pool - tenant-serving, non-billable',
    },
    {
      value: 'storage',
      label: 'storage',
      description: 'RustFS / object-store host - never receives tenants',
    },
    {
      value: 'infra',
      label: 'infra',
      description: 'Postgres / LB / internal services - never receives tenants',
    },
    {
      value: 'control-plane',
      label: 'control-plane',
      description: 'The host that runs OCP itself - patched last, delayed reboot, never receives tenants',
    },
  ];
}

export function isTenantServingPool(poolTag: string, demoPoolTag: string = DEFAULT_DEMO_POOL_TAG): boolean {
  return poolTag === 'tenant-pool' || poolTag === demoPoolTag || poolTag.startsWith('dedicated-');
}

export function isNonTenantPool(poolTag: string): boolean {
  return poolTag === 'infra' || poolTag === 'storage' || poolTag === 'control-plane';
}

/**
 * The monitoring role a pool tag implies, or `null` when the tag is
 * unconstrained (operator picks freely). Tenant-serving pools imply
 * 'tenant'; the 'storage'/'infra' pool tags imply the matching role.
 */
export function presetRoleForPoolTag(
  poolTag: string,
  demoPoolTag: string = DEFAULT_DEMO_POOL_TAG,
): ServerRole | null {
  if (isTenantServingPool(poolTag, demoPoolTag)) return 'tenant';
  if (poolTag === 'storage') return 'storage';
  if (poolTag === 'infra') return 'infra';
  if (poolTag === 'control-plane') return 'control-plane';
  return null;
}

/** True when the (poolTag, role) pair is inconsistent per the rule. */
export function isPoolRoleMismatch(
  poolTag: string,
  role: ServerRole,
  demoPoolTag: string = DEFAULT_DEMO_POOL_TAG,
): boolean {
  if (isTenantServingPool(poolTag, demoPoolTag) && role !== 'tenant') return true;
  if (isNonTenantPool(poolTag) && role === 'tenant') return true;
  // OCP-807 - the control-plane pair is coupled both ways.
  if ((poolTag === 'control-plane') !== (role === 'control-plane')) return true;
  return false;
}

/**
 * True when the role selector should be locked to 'tenant'. Only
 * tenant-serving pools force a single valid role; non-tenant pools still
 * allow either 'storage' or 'infra', so they stay editable (just
 * preset).
 */
export function isRoleLockedByPool(poolTag: string, demoPoolTag: string = DEFAULT_DEMO_POOL_TAG): boolean {
  return isTenantServingPool(poolTag, demoPoolTag);
}
